import { Component, OnInit } from '@angular/core'
import { MessagesService } from '../messages/messages.service'
import { ConfirmationService, SelectItem } from 'primeng/api'
import { ReceptionDetails, SampleReceptionDetailsDto, SampleReceptionStatus } from './model/sample-reception-details-dto'
import { SelectedOrderDetailsDto } from './model/selected-order-details-dto'
import { NgsSampleArrivalService } from './ngs-sample-arrival.service'
import { BBResponseContainer } from '../common-services/response-container'
import { ErrorHandler } from '../utils/error-handler'
import { PrintPlateLabelService } from '../print-plate-labels/print-plate-labels-service'
import { SelectableLab } from '../masterdata/models/lab'
import { Printer } from '../masterdata/models/printer'
import { PrintTemplateUtil } from '../common-services/print-template-utils'
import { DirectPrintRequest } from '../print-plate-labels/ngs-print-request'
import { BatchProcessService } from '../ngs-worklist-batch-process/batch-process.service'

@Component({
  selector: 'ngs-sample-arrival',
  templateUrl: './ngs-sample-arrival.component.html',
  styleUrls: ['./ngs-sample-arrival.component.css']
})

export class NgsSampleArrivalComponent implements OnInit {
  errorHandler: ErrorHandler = new ErrorHandler
  blocked = false
  puid: string
  scannedSampleBarcode: string
  scannedSampleList: SampleReceptionDetailsDto[] = []
  selectedScannedSamples: SampleReceptionDetailsDto[] = []
  showEditSampleDialog: boolean
  selectedSampleIssue: SampleReceptionStatus
  selectedSampleComment : string
  sampleIssuesOptions: any[]
  showReceiveSamplesDialog: boolean
  showCancelSamplesDialog: boolean
  arrivalTempOptions: any[]
  storageTempOptions: any[]
  storageLocationsOptions: any[]
  nextStepsOptions: any[]
  orderIssuesOptions: any[]
  rgcOptions: any[]
  rgcSelectionOptions: any[]
  selectedOrderArrivalTemp: string
  selectedOrderStorageTemp: string
  selectedOrderStorageLocation: string
  selectedOrderIssues: string[]
  rgcAvailable: boolean | null
  newReceptionComment: string
  samplesReceived: boolean
  selectedOrderDetails: SelectedOrderDetailsDto
  scannedUnreceivedSampleCount: number = 0;
  scannedReceivedSampleCount: number = 0;
  unreceivedSampleCount: number = 0;
  receivedSampleCount: number = 0;
  printerList: SelectItem[] = [];
  currentLab: SelectableLab;
  selectedPrinter: Printer = null;
  libTypeDataMap = new Map();
  sampleReceivedMessage: string;
  sampleBarcode: string;
  scannedPlateBarcode: string;
  scannedSampleBarcodeSet = new Set()
  unreceivedSamplesCountGroupedByPlate = new Map()
  cancellationErrorMessage: string;
  readonly minorIssueConstant = 'Minor Issue';
  readonly RECEIVE_SAMPLES_CONFIRMATION = 'Confirm Receive Samples';
  readonly EXCLAMATION_ICON_CLASS = 'fa fa-exclamation-circle';
  constructor(private messagesService: MessagesService,
              private confirmationService: ConfirmationService,
              private sampleArrivalService: NgsSampleArrivalService,
              private printPlateLabelService: PrintPlateLabelService,
              private printTemplateUtil: PrintTemplateUtil,
              private batchProcessService: BatchProcessService) {
  }

  mapListToLabelValue(list: string[]) {
    return list.map((option) => ({
      label: option,
      value: option
    }))
  }

  ngOnInit() {
    this.currentLab = JSON.parse(localStorage.getItem('lab'));
    this.sampleIssuesOptions = [
      { label: 'Sample is OK', value: SampleReceptionStatus.RECEIVED },
      { label: 'Sample is destroyed', value: SampleReceptionStatus.DESTROYED },
      { label: 'Sample is empty', value: SampleReceptionStatus.EMPTY },
      { label: 'Barcode became loose', value: SampleReceptionStatus.BARCODE_LOOSE },
      {label: this.minorIssueConstant, value: SampleReceptionStatus.MINOR_ISSUE}
    ]

    this.arrivalTempOptions = this.mapListToLabelValue(['Room temperature', 'Coolpacks', 'Dry Ice'])
    this.storageTempOptions = this.mapListToLabelValue(['Room temperature', 'Fridge', '-20°C Freezer', '-80°C Freezer'])
    this.storageLocationsOptions = this.mapListToLabelValue(['Unpacking Station', 'Station 1 (prePCR)', 'Station 4 (postPCR)',
      'Extraction S1', 'Extraction S2'])
    this.rgcOptions = [
      { label: 'Not applicable', value: null },
      { label: 'No', value: false },
      { label: 'Yes', value: true },
    ]

    this.nextStepsOptions = [
      { label: 'Entry QC', value: 'entry_qc_start' },
      { label: 'Start Lib Prep', value: 'library_preparation_through_bb' }
    ]

    this.orderIssuesOptions = this.mapListToLabelValue([
      'Packaging destroyed',
      'Arrival temperature wrong',
      'Wrong material sent',
      'Wrong number of samples',
      'Barcode(s) became loose',
      this.minorIssueConstant])

    this.selectedSampleIssue = SampleReceptionStatus.RECEIVED
    this.rgcAvailable = null
    this.puid = localStorage.getItem('PUID')
    this.samplesReceived = false;
    this.serviceCallForPrinterList();
    this.getLibTypesData();
  }

  scanSampleBarcode() {
    if (this.scannedSampleBarcode && !this.isDuplicateScan()) {
      this.getOrderDetailsBySampleBarcode()
    }
  }

  getOrderDetailsBySampleBarcode() {
    this.blocked = true
    this.sampleArrivalService.getSampleDetails(this.currentLab.lab._id,this.scannedSampleBarcode, this.selectedOrderDetails?.orderNumber)
    .subscribe(
      (response: BBResponseContainer) => {
        const { data, bbApiError } = response
        if (bbApiError) {
          this.showError(bbApiError)
          return
        }
        this.blocked = false
        const sample: SampleReceptionDetailsDto = data
        this.sampleBarcode = sample.barcode;
        if (sample.cancellationRequested) {
          this.cancellationErrorMessage = `Sample ${this.sampleBarcode} can't be received as it has been cancelled.`
          this.showCancelSamplesDialog = true
        } else {
          this.addNewSampleToTheScannedSamples(sample)
          if(sample.plateBarcode) {
            this.unreceivedSamplesCountGroupedByPlate.set(sample.plateBarcode, sample.plateUnreceivedSampleCount)
            this.messagesService.addErrorMessage({
              severity:'warn', summary:'Sample is on a plate',
              detail:' please scan the plate to receive all samples of this plate.'
            })
          }
       }
       this.scannedSampleBarcode = undefined
      },
      (error: any) => {
        this.showError(error)
      })
  }

  isSampleListEmpty() {
    return this.scannedSampleList.length == 0
  }

  addNewSampleToTheScannedSamples(sample: SampleReceptionDetailsDto) {
    if(!sample.receptionDetails) {
      sample.receptionDetails = {
        sampleStatus: SampleReceptionStatus.RECEIVED
      }
    }
    this.updateDisplayOrderDetails(sample)
    this.scannedSampleList.unshift(sample)
    if(!this.scannedSampleBarcodeSet.has(sample.barcode)) {
      if (!sample.receptionDetails.nextStep) {
        this.scannedUnreceivedSampleCount++;
      } else {
        this.scannedReceivedSampleCount++;
      }
    }
    this.scannedSampleBarcodeSet.add(sample.barcode)

  }

  updateDisplayOrderDetails(sample: SampleReceptionDetailsDto) {
    this.selectedOrderDetails = {
      orderNumber: sample.orderNumber,
      customerName: sample.customerName,
      sampleCount: sample.orderSampleCount,
      isGxp: sample.gxp,
      orderComment: sample.orderComment
    }
    if(sample.unreceivedSamplesCount != null) {
      this.unreceivedSampleCount = sample.unreceivedSamplesCount
      this.receivedSampleCount = sample.orderSampleCount - this.unreceivedSampleCount
    }
  }

  isDuplicateScan() {
    if (this.scannedSampleBarcodeSet.has(this.scannedSampleBarcode)) {
      this.messagesService.addErrorMessage(
        {
          severity: 'warn',
          summary: 'Duplicate Scan',
          detail: `Barcode ${this.scannedSampleBarcode} has already been scanned.`
        })
      this.focusOnBarcodeBox()
      return true
    }
    return false;
  }

  editSample() {
    this.getSelectedSampleStatus()
    this.showEditSampleDialog = true
  }

 getSelectedSampleStatus() {
    let status = this.selectedScannedSamples[0].receptionDetails?.sampleStatus;
    this.selectedSampleIssue = status ? status : SampleReceptionStatus.RECEIVED;
 }

  saveEditedSample() {

    if(this.selectedSampleIssue == 'MINOR_ISSUE'){
      if(this.selectedSampleComment == undefined || this.selectedSampleComment.length == 0 ||this.selectedSampleComment.replace(/ /g, "").length == 0 || this.selectedSampleComment.replace(/(\r\n|\n|\r)/gm, "").length == 0){
         this.messagesService.addErrorMessage({
          severity:'error', summary:'Please enter comment!',
          detail:'Minor Issue must be saved with a comment.'
        })
        return;
      }

    }
    this.selectedScannedSamples.forEach(sample => {
      sample.receptionDetails.sampleStatus = this.selectedSampleIssue;
      if(this.selectedSampleComment && this.selectedSampleComment.length > 0){
        sample.receptionDetails.sampleIssueComment = this.selectedSampleComment;
      }
    })
    this.showEditSampleDialog = false;
  }

  confirmSampleRemoval() {
    if (this.selectedScannedSamples.length > 0) {
      let message = 'Selected samples will be deleted. Do you wish to proceed?'
      this.confirmationService.confirm({
        message: message,
        header: 'Confirm Sample Deletion',
        accept: () => {
          this.removeSelectedSamples()
        },
        reject: () => {
          this.clearSelection()
        }
      })
    }
  }

  removeSelectedSamples() {
    this.scannedSampleList = this.scannedSampleList.filter(sample => !this.selectedScannedSamples.includes(sample))
    this.selectedScannedSamples.forEach(sample=>{
      if(sample.receptionDetails.nextStep) {
        this.scannedReceivedSampleCount--;
      } else {
        this.scannedUnreceivedSampleCount--;
      }
      this.scannedSampleBarcodeSet.delete(sample.barcode)
    })
    this.clearSelection()
  }

  clearSelection() {
    this.selectedScannedSamples = []
  }

  receiveSamples() {
    this.rgcSelectionOptions = [...this.rgcOptions]
    this.rgcAvailable = null
    let atLeastOneSampleWithExtraction = false
    const scannedUnreceivedPlateSampleCountMap = new Map()
    this.scannedSampleList.forEach(sample=>{
      if(sample.extraction) {
        atLeastOneSampleWithExtraction = true;
      }
      if(sample.plateBarcode && !sample.receptionDetails.nextStep) {
        if(scannedUnreceivedPlateSampleCountMap.has(sample.plateBarcode)) {
          let sampleCount = scannedUnreceivedPlateSampleCountMap.get(sample.plateBarcode)
          sampleCount++;
          scannedUnreceivedPlateSampleCountMap.set(sample.plateBarcode, sampleCount)
        } else {
          scannedUnreceivedPlateSampleCountMap.set(sample.plateBarcode, 1)
        }
      }
    })
    if(atLeastOneSampleWithExtraction) {
      this.rgcSelectionOptions.splice(0,1)
    }

    this.showWarningOfPlateUnscannedSamplesIfPresent(scannedUnreceivedPlateSampleCountMap)

  }

  private showWarningOfPlateUnscannedSamplesIfPresent(scannedUnreceivedPlateSampleCountMap: Map<any, any>) {
    const platesWithSamplesMissedInScanning = this.getPlatesHavingMissingSamplesInScanning(scannedUnreceivedPlateSampleCountMap)
    if(platesWithSamplesMissedInScanning.length > 0) {
      this.confirmationService.confirm({
        message: `Warning! Some, but not all samples of plate(s) ${Array.from(platesWithSamplesMissedInScanning).join(", ")} are listed for reception. Click "Cancel" to go back and scan the plate barcode(s) to receive all samples, or click "Receive selected samples" to receive the samples that are listed currently.`,
        header: this.RECEIVE_SAMPLES_CONFIRMATION,
        icon: this.EXCLAMATION_ICON_CLASS,
        acceptLabel:'Receive selected samples',
        rejectLabel: 'Cancel',
        accept: () => {
          this.showReceiveSamplesDialogBasedOnScannedCount();
        },
        reject: () => {
        }
      })
    } else {
      this.showReceiveSamplesDialogBasedOnScannedCount();
    }
  }

  private showReceiveSamplesDialogBasedOnScannedCount() {
    if (this.scannedReceivedSampleCount > 0) {
      this.confirmationService.confirm({
        key: 'scanned-samples',
        message: 'Warning! Some of the scanned samples are already received. Only the unreceived samples will be processed. Proceed anyway?',
        header: this.RECEIVE_SAMPLES_CONFIRMATION,
        icon: this.EXCLAMATION_ICON_CLASS,
        accept: () => {
          this.showReceiveSamplesDialog = true
        },
        reject: () => {
        }
      })
    } else {
      this.showReceiveSamplesDialog = true
    }
  }


  saveSampleData() {
    if(this.selectedOrderIssues?.includes(this.minorIssueConstant)) {
      if(this.selectedOrderIssues.length > 1) {
        this.messagesService.addErrorMessage({
          severity:'error', summary:'Invalid Order Issue Selection!',
          detail:'Minor Issue must not be combined with any other Order Issue. Either Select only Minor Issue option or de-select it.'
        })
        return;
      }
      if(!this.newReceptionComment || this.newReceptionComment.trim() == '') {
        this.messagesService.addErrorMessage({
          severity:'error', summary:'Invalid Order Issue Selection!',
          detail:'Minor Issue was selected, please specify details in the Order Comment.'
        })
        return;
      }
    }

    const confirmationMessage = this.unreceivedSampleCount != this.scannedUnreceivedSampleCount?
      'Scanned sample number does not match expected number of samples for this order. Proceed anyway?' :
      'Please confirm sample reception'

    this.confirmationService.confirm({
      message: confirmationMessage,
      header: this.RECEIVE_SAMPLES_CONFIRMATION,
      icon: this.EXCLAMATION_ICON_CLASS,
      accept: () => {
        this.updateScannedSampleDetails()
        this.saveScannedSamples()
        this.samplesReceived = true;
      },
      reject: () => {
      }
    })
    this.showReceiveSamplesDialog = false
  }

  updateScannedSampleDetails() {
    this.scannedSampleList.forEach((sample: SampleReceptionDetailsDto) => {
        let receptionDetails = sample.receptionDetails
        receptionDetails.userId = this.puid
        receptionDetails.arrivalTemp = this.selectedOrderArrivalTemp
        receptionDetails.storageTemp = this.selectedOrderStorageTemp
        receptionDetails.storageLocation = this.selectedOrderStorageLocation
        receptionDetails.nextStep = this.determineNextStepFromLibType(sample.libraryType)
        receptionDetails.receptionComment = this.newReceptionComment;
        receptionDetails.orderIssues = this.selectedOrderIssues
        receptionDetails.rgcAvailable = this.rgcAvailable
      }
    )
  }

  determineNextStepFromLibType(libraryType: string): string {
    return this.libTypeDataMap.has(libraryType) && this.libTypeHasEntryQC(this.libTypeDataMap.get(libraryType)) ? "entry_qc_start" : "library_preparation_through_bb";
  }

  libTypeHasEntryQC(libType: any) {
    let libTypeHasEntryQCProcess = false
    libType.process?.forEach(process=>{
      if(process.includes("Entry_QC")) {
        libTypeHasEntryQCProcess = true
      }
    })
    return libTypeHasEntryQCProcess
  }

  saveScannedSamples() {
    this.blocked = true
    this.sampleArrivalService.saveSampleReceptionDetails(this.scannedSampleList)
    .subscribe(
      () => {
        this.messagesService.addSuccessMessage({
          severity: 'success', summary: 'Samples Received',
          detail: 'Samples for order ' + this.selectedOrderDetails.orderNumber + ' have been received!'
        })
        this.blocked = false
        this.sampleReceivedMessage = this.getSamplesReceivedSuccessMessage()
        this.unreceivedSampleCount -= this.scannedUnreceivedSampleCount;
        this.receivedSampleCount += this.scannedUnreceivedSampleCount;
        this.scannedReceivedSampleCount = this.scannedUnreceivedSampleCount = 0;
      },
      error => {
        this.showError(error)
      }
    )
  }

  getSamplesReceivedSuccessMessage() {
    const sampleCount = this.scannedUnreceivedSampleCount;
    const message = (sampleCount == 1 ? "sample" : "samples") + " successfully received" +
      " on " + new Date().toDateString() +
      " by " + this.puid;
    return sampleCount + " " + message;
  }

  sampleReceptionInformationFilled() {
    return !(this.selectedOrderArrivalTemp &&
      this.selectedOrderStorageTemp &&
      this.selectedOrderStorageLocation &&
      this.checkForRGCAvailable())
  }

  private checkForRGCAvailable() {
     if(this.rgcSelectionOptions.length == 2) {
      return this.rgcAvailable != undefined
    } else {
      return true
    }
  }

  focusOnBarcodeBox() {
    let element = document.getElementById('barcodeBox')
    if (element) {
      element.focus()
    }
  }

  showError(error: any) {
    this.blocked = false
    let message = this.errorHandler.handleError(error)
    this.messagesService.addErrorMessage(message)
  }

  serviceCallForPrinterList() {
    this.printerList = [];
    this.printPlateLabelService.getPrintersList(this.currentLab.lab, true).subscribe(
      results => {
        if (!results) {
          return;
        }
        for (let result of results.data) {
          if (result.template) {
            if ('Tube' == result.template.entity) {
              this.printerList.push({ label: result.name, value: result});
            }
          }
        }
      },
      error => {
        this.showError(error);
      });
  }

  getLibTypesData() {
    let libTypeDataList = [];
    this.libTypeDataMap = new Map();
    this.batchProcessService.getLibraryTypeData(this.currentLab.lab._id).subscribe(
      results => {
        libTypeDataList = results.data;
        this.createMapOfLibTypes(libTypeDataList);
      },
      error => {
        this.showError(error)
      }
    );
  }

  createMapOfLibTypes(libTypeDataList: any[]) {
    if(libTypeDataList && libTypeDataList.length > 0) {
      libTypeDataList.forEach(library => {
        this.libTypeDataMap.set(library['name'], library);
      })
    }
  }

  isPrintDisabled() {
    return (!this.selectedPrinter || this.scannedSampleList.length == 0);
  }

  printSamples() {
    if(!this.selectedPrinter.template
      || !this.selectedPrinter.template.format
      || this.selectedPrinter.template.format.trim() == '') {
      this.messagesService.addErrorMessage({severity: 'error', summary: 'Invalid printer template',
        detail: 'Printer template format string appears to be empty. Please fill in the ' +
        'command string in template and reassign it to the printer to continue.'});
      return;
    }
    let formatStringOriginal = this.selectedPrinter.template.format;
    if(!this.printTemplateUtil.doesTemplateHaveToken(formatStringOriginal, this.printTemplateUtil.BARCODE_TOKEN)) {
      this.messagesService.addErrorMessage({severity: 'error', summary: 'Invalid printer template',
        detail: 'Could not find barcode token in template. Please fill in the ' +
        'format string correctly in template and reassign it to the printer to continue.'});
      return;
    }

    let directPrintRequest = new DirectPrintRequest();
    directPrintRequest.printer = this.selectedPrinter;
    directPrintRequest.directPrintCommands = [];

    const numberOfLanes = this.selectedPrinter.template.numberOfParallelLanes;
    for(let i = 0; i < this.scannedSampleList.length; i = i + numberOfLanes) {

      const samplesOfCurrentLane = this.scannedSampleList.slice(i, i + numberOfLanes)
      let formatString = formatStringOriginal;
      for(let j = 0; j < samplesOfCurrentLane.length; j++) {
        let sample = samplesOfCurrentLane[j]
        formatString = this.printTemplateUtil.replaceToken(formatString, `#${this.printTemplateUtil.BARCODE_TOKEN}_${(j+1)}#`, sample.barcode);
        formatString = this.printTemplateUtil.replaceToken(formatString, `#${this.printTemplateUtil.PROJECT_TOKEN}_${(j+1)}#`, sample.orderNumber);
        formatString = this.printTemplateUtil.replaceToken(formatString, `#${this.printTemplateUtil.SAMPLE_NAME_TOKEN}_${(j+1)}#`, sample.sampleName);
        formatString = this.printTemplateUtil.replaceToken(formatString, `#${this.printTemplateUtil.LIBRARY_TYPE_TOKEN}_${(j+1)}#`, sample.libraryType);
      }
      directPrintRequest.directPrintCommands.push(formatString);
    }

    this.blocked = true;
    this.sampleArrivalService.printSampleLabels(directPrintRequest).subscribe(
      result => {
        this.blocked = false;
        if(result.data) {
          this.messagesService.addSuccessMessage({severity: 'success', summary: result.data});
        }
      },
      error => {
        this.showError(error);
      }
    );

  }

  getCSSClassForReceivedSamples(receptionDetails:ReceptionDetails) {
    return receptionDetails.nextStep  ? 'received-sample-color' : ''
  }

  scanPlateBarcode() {
    if(this.scannedPlateBarcode?.trim() === "") {
      return;
    }

    this.blocked = true;
    this.sampleArrivalService.getSampleDetailsByPlateBarcode(this.currentLab.lab._id, this.scannedPlateBarcode, this.selectedOrderDetails?.orderNumber).subscribe(
      {
        next:result=>{
          this.blocked = false;
          const cancellationRequestedSamples: string[] = [];
          const incomingSampleBarcodes = new Set(Array.from(result.map(sample=>sample.barcode)))
          this.scannedSampleList = this.scannedSampleList.filter(sample=> !incomingSampleBarcodes.has(sample.barcode))
          result.forEach(sample=> {
              if(sample.cancellationRequested) {
                cancellationRequestedSamples.push(sample.barcode)
              } else {
                this.addNewSampleToTheScannedSamples(sample);
              }
          })

          this.unreceivedSamplesCountGroupedByPlate.set(this.scannedPlateBarcode, result[0].plateUnreceivedSampleCount)
          if(result.length !== result[0].containerSampleCount) {
            this.messagesService.addErrorMessage({
              severity:'warn', summary:'Some samples missing!',
              detail: 'Some samples of this plate are missing in BB!'
            })
          }
          if(cancellationRequestedSamples.length > 0) {
            this.unreceivedSamplesCountGroupedByPlate.set(this.scannedPlateBarcode, result[0].plateUnreceivedSampleCount - cancellationRequestedSamples.length)
            this.prepareCancellationErrorMessageForPlateSamples(cancellationRequestedSamples, result)
            this.showCancelSamplesDialog = true;
          }
          this.scannedPlateBarcode = null

        }, error:error=>{
          this.showError(error);
        }
      }
    )
  }

  private prepareCancellationErrorMessageForPlateSamples(cancellationRequestedSamples: string[], result: SampleReceptionDetailsDto[]) {
    if(result.length === cancellationRequestedSamples.length) {
      this.cancellationErrorMessage = "All the samples of this plate have been cancelled and can't be received";
    } else if(cancellationRequestedSamples.length > 5) {
      this.cancellationErrorMessage = cancellationRequestedSamples.length + " samples of this plate have been cancelled and can't be received";
    } else {
      this.cancellationErrorMessage = `Samples ${cancellationRequestedSamples.join(",")} can't be received as they have been cancelled`;
    }
  }

  private getPlatesHavingMissingSamplesInScanning(scannedUnreceivedPlateSampleCountMap: Map<string, number>) {
    const platesWithSamplesMissedInScanning: string[] = [];
    scannedUnreceivedPlateSampleCountMap.forEach((sampleCount: number, plateBarcode: string)=>{
      if(this.unreceivedSamplesCountGroupedByPlate.get(plateBarcode) !== sampleCount) {
        platesWithSamplesMissedInScanning.push(plateBarcode)
      }
    })
    return platesWithSamplesMissedInScanning;
  }
}
